import { z } from "zod";

/**
 * Treść materiału pomocy — kanonicznie wartość edytora (Slate JSON), tak jak
 * leży w `help_article.content`.
 *
 * Zestaw węzłów jest zamknięty i celowo krótki: każdy dodatkowy typ to kolejny
 * komponent w rendererze klientek i kolejny wariant do sprawdzenia na 390 px
 * w dwóch motywach. Schema jest jedna dla wszystkich, którzy piszą do bazy:
 * panelu, agenta ze Slacka i skryptu lokalnego.
 */
export const HELP_BLOCK_TYPES = ["p", "h2", "h3", "ul", "ol", "callout", "steps", "img", "video"] as const;
export type HelpBlockType = (typeof HELP_BLOCK_TYPES)[number];

const text = z.string().trim().min(1);

export const helpTextSchema = z.object({
  text: z.string(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
});

export const helpLinkSchema = z.object({
  type: z.literal("a"),
  url: z.string().min(1),
  children: z.array(helpTextSchema).min(1),
});

export const helpInlineSchema = z.union([helpLinkSchema, helpTextSchema]);
export type HelpInline = z.infer<typeof helpInlineSchema>;

const inlineChildren = z.array(helpInlineSchema).min(1);

export const helpParagraphSchema = z.object({
  type: z.enum(["p", "h2", "h3"]),
  children: inlineChildren,
});

export const helpListItemSchema = z.object({ type: z.literal("li"), children: inlineChildren });

export const helpListSchema = z.object({
  type: z.enum(["ul", "ol"]),
  children: z.array(helpListItemSchema).min(1),
});

/** Dzisiejszy `note`: wyróżniona uwaga z własnym tytułem. */
export const helpCalloutSchema = z.object({
  type: z.literal("callout"),
  title: text,
  children: inlineChildren,
});

export const helpStepSchema = z.object({
  type: z.literal("step"),
  title: text,
  children: inlineChildren,
});

/**
 * Trzy do pięciu kroków to trzon każdej instrukcji, więc kroki są osobnym
 * węzłem, a nie listą punktowaną — numeracja, tytuły i odstępy mają się
 * renderować tak samo w każdym materiale.
 */
export const helpStepsSchema = z.object({
  type: z.literal("steps"),
  children: z.array(helpStepSchema).min(1),
});

const voidChildren = z.array(z.object({ text: z.literal("") })).length(1);

export const helpImageSchema = z.object({
  type: z.literal("img"),
  storagePath: z.string().min(1),
  alt: text,
  caption: text,
  // Wymiary RZECZYWISTE pliku. Bez nich układ strony skacze w trakcie
  // dogrywania obrazka, więc nie służą do skalowania.
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  // Szerokość wyświetlania w procentach kolumny tekstu — pionowy zrzut
  // z telefonu na pełnej szerokości zjada cały ekran. Osobno od `width`
  // właśnie po to, żeby ramka obrazu dalej znała proporcje pliku.
  maxWidth: z.number().int().min(20).max(100).optional(),
  children: voidChildren,
});

export const helpVideoSchema = z.object({
  type: z.literal("video"),
  storagePath: z.string().min(1),
  poster: z.string().min(1),
  captions: z.string().min(1),
  title: text,
  // Klientka ma zrozumieć film bez dźwięku i przeczytać instrukcję bez filmu —
  // stąd napisy i opis tekstowy są wymagane, nie opcjonalne.
  transcript: text,
  durationSeconds: z.number().positive(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  children: voidChildren,
});

export const helpBlockSchema = z.discriminatedUnion("type", [
  helpParagraphSchema,
  helpListSchema,
  helpCalloutSchema,
  helpStepsSchema,
  helpImageSchema,
  helpVideoSchema,
]);

export type HelpBlock = z.infer<typeof helpBlockSchema>;

export const helpContentSchema = z.array(helpBlockSchema);
export type HelpContent = z.infer<typeof helpContentSchema>;

export const EMPTY_HELP_CONTENT: HelpContent = [{ type: "p", children: [{ text: "" }] }];

/**
 * Renderer klientek pomija węzeł, którego nie zna, zamiast wywalać stronę:
 * materiał zapisany nowszą wersją edytora niż wdrożony client-web ma się
 * wyświetlić bez tego jednego bloku.
 */
export const parseHelpContent = (value: unknown): HelpContent => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((block) => {
    const parsed = helpBlockSchema.safeParse(block);
    return parsed.success ? [parsed.data] : [];
  });
};

// Szkic może mieć bloki niedokończone: zrzut wklejony bez opisu i podpisu (i bez
// wymiarów, gdy plik ich nie podał), film bez plakatu i napisów, krok bez tytułu.
// Luzujemy wyłącznie te pola - drzewo węzłów dalej przechodzi walidację.
const draftText = z.string();
const draftDimension = z.number().int().nonnegative();

const helpDraftBlockSchema = z.discriminatedUnion("type", [
  helpParagraphSchema,
  helpListSchema,
  helpCalloutSchema.extend({ title: draftText }),
  helpStepsSchema.extend({ children: z.array(helpStepSchema.extend({ title: draftText })).min(1) }),
  helpImageSchema.extend({ alt: draftText, caption: draftText, width: draftDimension, height: draftDimension }),
  helpVideoSchema.extend({
    poster: draftText,
    captions: draftText,
    title: draftText,
    transcript: draftText,
    durationSeconds: z.number().nonnegative(),
  }),
]);

/**
 * Treść do edytora w panelu. W odróżnieniu od `parseHelpContent` zostawia bloki
 * niedokończone - obraz bez opisu, film bez plakatu - bo szkic ma je mieć,
 * a bramka publikacji i tak ich nie przepuści. Filtr renderera w tym miejscu
 * po cichu wycinał wklejony zrzut przy ponownym otwarciu, a kolejny zapis
 * utrwalał szkic już bez niego (TRE-2257). Blok o zepsutej strukturze (np.
 * `children: [null]`) dalej odpada, żeby nie wywalić edytora.
 */
export const parseHelpDraftContent = (value: unknown): HelpContent => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.flatMap((block) => {
    const parsed = helpDraftBlockSchema.safeParse(block);
    return parsed.success ? [parsed.data as HelpBlock] : [];
  });
};

export const helpContentMedia = (content: HelpContent): string[] =>
  content.flatMap((block) => {
    if (block.type === "img") return [block.storagePath];
    if (block.type === "video") return [block.storagePath, block.poster, block.captions];
    return [];
  });

const inlineText = (children: HelpInline[]): string =>
  children.map((child) => ("text" in child ? child.text : inlineText(child.children))).join("");

/** Lustro triggera `help_article_search_text` — używane w testach i w podglądzie. */
export const helpContentText = (content: HelpContent): string =>
  content
    .flatMap((block) => {
      switch (block.type) {
        case "p":
        case "h2":
        case "h3":
          return [inlineText(block.children)];
        case "ul":
        case "ol":
          return block.children.map((item) => inlineText(item.children));
        case "callout":
          return [block.title, inlineText(block.children)];
        case "steps":
          return block.children.flatMap((step) => [step.title, inlineText(step.children)]);
        case "img":
          return [block.alt, block.caption];
        case "video":
          return [block.title, block.transcript];
        default:
          return [];
      }
    })
    .filter(Boolean)
    .join(" ");

/**
 * Treść materiału spłaszczona do tekstu Z ZACHOWANĄ strukturą — dla czytelnika,
 * który nie renderuje JSON-a, dziś doradczyni klientek (TRE-2154).
 *
 * `helpContentText` powyżej skleja wszystko spacjami, bo jest lustrem triggera
 * wyszukiwarki. Model dostający taki ciąg gubi to, co w instrukcji jest
 * najważniejsze: KOLEJNOŚĆ kroków. Stąd druga funkcja zamiast parametru —
 * jedna odpowiada bazie, druga człowiekowi.
 */
export const helpContentToPlainText = (content: HelpContent): string =>
  content
    .flatMap((block): string[] => {
      switch (block.type) {
        case "p":
          return [inlineText(block.children)];
        case "h2":
          return [`## ${inlineText(block.children)}`];
        case "h3":
          return [`### ${inlineText(block.children)}`];
        case "ul":
          return [block.children.map((item) => `- ${inlineText(item.children)}`).join("\n")];
        case "ol":
          return [block.children.map((item, index) => `${index + 1}. ${inlineText(item.children)}`).join("\n")];
        case "callout":
          return [`Uwaga — ${block.title}: ${inlineText(block.children)}`];
        case "steps":
          return [
            block.children
              .map((step, index) => `Krok ${index + 1}. ${step.title}: ${inlineText(step.children)}`)
              .join("\n"),
          ];
        // Zrzut ekranu niesie treść wyłącznie w opisie i podpisie — bez nich
        // instrukcja czytana tekstem ma dziurę dokładnie tam, gdzie klientka
        // ma coś kliknąć.
        case "img":
          return [`[Zrzut ekranu: ${block.alt}${block.caption ? ` — ${block.caption}` : ""}]`];
        case "video":
          return [`[Film „${block.title}"] ${block.transcript}`];
        default:
          return [];
      }
    })
    .filter(Boolean)
    .join("\n\n");
